import type { ReactNode } from 'react';
import { Bell, Ban, Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import {
  HWCG_GROUP_ACTION_LABELS,
  HWCG_GROUP_IDS,
  HWCG_GROUP_LABELS,
  hwcgGroupActionValues,
  setHwcgGroupAction,
  type HwcgGroupActionValue,
  type HwcgGroupActionsState,
  type HwcgGroupId,
} from './hwcgGroupActions';

const ACTION_ICON: Record<HwcgGroupActionValue, typeof Zap> = {
  activate: Zap,
  notify: Bell,
  ['no_action']: Ban,
};

/**
 * One selected constituency at a time. EOC and Responsible Party can be
 * activated; Mutual Aid and Service Provider can only be notified or left with
 * no action. Nothing is preselected, so an unanswered group stays "Not set".
 */
export function HwcgGroupActionsEditor({
  actions,
  onChange,
  selectedId,
  onSelect,
  noticePanel,
  issues,
}: {
  actions: HwcgGroupActionsState;
  onChange: (next: HwcgGroupActionsState) => void;
  selectedId: HwcgGroupId;
  onSelect: (groupId: HwcgGroupId) => void;
  noticePanel?: ReactNode;
  issues?: Partial<Record<HwcgGroupId, string>>;
}) {
  const selectedAction = actions[selectedId];
  const showNotice =
    selectedAction === 'activate' || selectedAction === 'notify';

  return (
    <div className="tw:space-y-4" data-tour="hwcg-group-actions">
      <div>
        <h3 className="tw:text-sm tw:font-semibold">
          Group Actions <span className="tw:text-destructive">*</span>
        </h3>
        <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
          Choose Activate, Notify, or No Action for each group. Mutual Aid and
          Service Provider support Notify or No Action only. Activation requests
          and notifications will be sent when you confirm creation of the
          incident.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="HWCG groups"
        className="tw:grid tw:grid-cols-2 tw:gap-2 tw:lg:grid-cols-4"
      >
        {HWCG_GROUP_IDS.map((groupId) => {
          const selected = groupId === selectedId;
          const action = actions[groupId];
          return (
            <button
              key={groupId}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onSelect(groupId)}
              className={cn(
                'tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-center tw:transition-colors',
                selected
                  ? 'tw:border-primary tw:bg-primary tw:text-primary-foreground'
                  : 'tw:border-border tw:bg-muted/40 tw:hover:bg-muted'
              )}
            >
              <span className="tw:block tw:text-sm tw:font-medium">
                {HWCG_GROUP_LABELS[groupId]}
              </span>
              <span
                className={cn(
                  'tw:mt-0.5 tw:block tw:text-[11px]',
                  issues?.[groupId]
                    ? selected
                      ? 'tw:text-primary-foreground'
                      : 'tw:text-destructive'
                    : selected
                      ? 'tw:text-primary-foreground/80'
                      : 'tw:text-muted-foreground'
                )}
              >
                {issues?.[groupId] ??
                  (action ? HWCG_GROUP_ACTION_LABELS[action] : 'Not set')}
              </span>
            </button>
          );
        })}
      </div>

      <div className="tw:space-y-3">
        <h4 className="tw:text-sm tw:font-semibold">
          {HWCG_GROUP_LABELS[selectedId]}
        </h4>
        <div className="tw:space-y-1.5">
          <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
            Group action
          </div>
          <div
            role="group"
            aria-label={`${HWCG_GROUP_LABELS[selectedId]} action`}
            className="tw:flex tw:flex-wrap tw:gap-1"
          >
            {hwcgGroupActionValues(selectedId).map((action) => {
              const selected = selectedAction === action;
              const Icon = ACTION_ICON[action];
              return (
                <Button
                  key={action}
                  type="button"
                  size="sm"
                  variant={selected ? 'default' : 'outline'}
                  aria-pressed={selected}
                  onClick={() =>
                    onChange(setHwcgGroupAction(actions, selectedId, action))
                  }
                >
                  <Icon className="tw:size-3.5" />
                  {HWCG_GROUP_ACTION_LABELS[action]}
                </Button>
              );
            })}
          </div>
          <p className="tw:text-xs tw:text-muted-foreground">
            Activate opens the execution UI below. Notify opens Notify. No
            Action hides it.
          </p>
        </div>
        {showNotice ? noticePanel : null}
      </div>
    </div>
  );
}
